import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

type OpenDeliveryNoteInMobileAppResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Opens a delivery note in the mobile app.
 *
 * @param assetId - The asset ID of the delivery note.
 * @returns A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Notification/operation/open_dln_push_open_dln_post
 */

export const openDeliveryNoteInMobileApp = async (assetId: UUID) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.OPEN_IN_MOBILE_APP(), {
      json: { assetId },
      validate: {
        body: z.object({
          assetId: z.uuid(),
        }),
      },
    })
    .json<OpenDeliveryNoteInMobileAppResponse>();

  return response;
};

/**
 * Custom hook for opening a delivery note in the mobile app.
 *
 * @returns An object containing the mutation function and related properties.
 */
export const useMutationOpenInMobileApp = (
  options?: Parameters<typeof useMutation>[0],
) => {
  return useMutation({
    mutationFn: async ({ assetId }: { assetId: UUID }) =>
      openDeliveryNoteInMobileApp(assetId),
    ...options,
  });
};
